import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";

import {
  createComplex,
  createCourtRoom,
  createDistrict,
  createEstablishment,
  updateComplex,
  updateCourtRoom,
  updateDistrict,
  updateEstablishment,
} from "../../../../api/courtApis";
import SearchableSelect, {
  type SelectOption,
} from "../../../../components/SearchableSelect";
import { FormField, Modal, TextInput } from "./ModalShell";
import type {
  CourtComplexRow,
  CourtRoomRow,
  DistrictRow,
  EstablishmentRow,
  JudgeOption,
} from "./types";
import DateInput from "react-calendar-ui-kit";
import { getTodayDate } from "../../../../utils/Date";
import { clsx } from "clsx";
import api from "../../../../lib/api";
import { indianStateList } from "../../../../utils/indianStateOptions";
import { useFormDirty } from "../../../../hooks/useFormDirty";

interface DistrictModalProps {
  district?: DistrictRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export const DistrictModal = ({
  district,
  onClose,
  onSaved,
}: DistrictModalProps) => {
  const [step, setStep] = useState(district ? 3 : 1);
  const [form, setForm] = useState({
    stateName: district?.stateName || "",
    districtName: district?.districtName || "",
    districtCode: district?.districtCode || "",
    headquarter: district?.headquarter || "",
    population: district?.population || "",
    status: district?.status || "Active",
  });

  const isDirty = useFormDirty(
  {
    stateName:     district?.stateName     || "",
    districtName:  district?.districtName  || "",
    districtCode:  district?.districtCode  || "",
    headquarter:   district?.headquarter   || "",
    population:    district?.population    || "",
    status:        district?.status        || "Active",
  },
  form
);
  const [isSaving, setIsSaving] = useState(false);
  const stateOptions = indianStateList.map((item) => item.state);

  const selectState = (stateName: string) => {
    setForm((prev) => ({
      ...prev,
      stateName,
    }));
  };

  const goNext = () => {
    if (step === 1 && !form.stateName) {
      toast.error("Select state");
      return;
    }

    if (step === 2 && !form.districtName) {
      toast.error("Select district");
      return;
    }
    if (step === 1) {
      setForm((prev) => ({
        ...prev,
        districtName: "",
      }));
    }
    if (step === 2) {
      setForm((prev) => ({
        ...prev,
        headquarter: "",
        status: "Active",
      }));
    }

    setStep((current) => Math.min(current + 1, 3));
  };

  const handleSave = async () => {
    if (district && !isDirty) { toast.info("No changes detected"); return; }
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!nameRegex.test(form.districtName.trim())) {
      toast.error("District Court Name should contain alphabets only");
      return;
    }

    if (!nameRegex.test(form.headquarter.trim())) {
      toast.error("Headquarters should contain alphabets only");
      return;
    }
    if (!form.stateName || !form.districtName || !form.headquarter) {
      toast.error("Fill all required district court fields");
      return;
    }

    try {
      setIsSaving(true);
      const districtCode =
        form.districtCode ||
        form.districtName
          .replace(/[^a-z]/gi, "")
          .slice(0, 3)
          .toUpperCase();
      const payload = {
        ...form,
        districtCode: districtCode.toUpperCase(),
      };
      const res = district
        ? await updateDistrict({ _id: district._id, ...payload })
        : await createDistrict(payload);

      if (res?.data?.status) {
        toast.success(res.data.message || "District saved");
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save district");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save district");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={district ? "Edit District Court" : "Add District Court"}
      subtitle="Court Settings · District Courts"
      onClose={onClose}
    >
      <StepIndicator step={step} />

      {step === 1 && (
        <div className="space-y-6">
          <p className="text-sm text-slate-500">
            Start by selecting the <span className="font-semibold">state</span>.
            This will narrow down the district list in the next step.
          </p>
          <FormField label="State" required>
            <select
              value={form.stateName}
              onChange={(event) => selectState(event.target.value)}
              className="h-12 w-full rounded-lg border border-[#E2E8F0] px-4 outline-none focus:border-[#f26422] focus:ring-2 focus:ring-orange-100"
            >
              <option value="">Select state...</option>
              {stateOptions.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="rounded-lg border border-[#fde8d8] bg-[#fff4ee] px-4 py-3 text-sm text-[#c24e14]">
            State: <span className="font-bold">{form.stateName}</span>
          </div>
          <FormField label="District" required>
            <input
              value={form.districtName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  districtName: event.target.value,
                }))
              }
              placeholder="Select district..."
              className="h-12 w-full rounded-lg border border-[#E2E8F0] px-4 outline-none focus:border-[#f26422] focus:ring-2 focus:ring-orange-100"
            />
          </FormField>
          <p className="text-sm text-slate-400">
            Districts are filtered by the selected state.
          </p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="rounded-lg border border-[#fde8d8] bg-[#fff4ee] px-4 py-3 text-sm text-[#c24e14]">
            State: <span className="font-bold">{form.stateName}</span>
            <span className="mx-5">·</span>District:{" "}
            <span className="font-bold">{form.districtName}</span>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextInput
              label="District Court Name"
              required
              value={form.districtName}
              onChange={(value) =>
                setForm({
                  ...form,
                  districtName: value.replace(/[^a-zA-Z\s]/g, ""),
                })
              }
              placeholder="District Court, Chennai"
              disabled
            />
            <TextInput
              label="Headquarters"
              required
              value={form.headquarter}
              onChange={(value) =>
                setForm({
                  ...form,
                  headquarter: value.replace(/[^a-zA-Z\s]/g, ""),
                })
              }
              placeholder="e.g. Pune City"
            />
            <FormField label="Status">
              <select
                value={form.status}
                onChange={(event) =>
                  setForm({ ...form, status: event.target.value })
                }
                className="h-12 w-full rounded-lg border border-[#E2E8F0] px-4 outline-none focus:border-[#f26422] focus:ring-2 focus:ring-orange-100 md:col-span-2"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </FormField>
          </div>
        </div>
      )}

      <WizardActions
        step={step}
        isSaving={isSaving}
        saveLabel={district ? "Update District Court" : "Save District Court"}
        onBack={() => setStep((current) => Math.max(current - 1, 1))}
        onCancel={onClose}
        onNext={goNext}
        onSave={handleSave}
          disabled={!!district && !isDirty} 
      />
    </Modal>
  );
};

const StepIndicator = ({ step }: { step: number }) => {
  const labels = ["Select State", "Select District", "Court Details"];

  return (
    <div className="-mx-8 mb-7 border-b border-[#E8EDF5] px-8 pb-6">
      <div className="grid grid-cols-[1fr_56px_1fr_56px_1fr] items-start gap-4">
        {labels.map((label, index) => {
          const number = index + 1;
          const done = step > number;
          const active = step === number;

          return (
            <div key={label} className="contents">
              <div className="text-center">
                <div
                  className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold ${
                    done
                      ? "border-[#f26422] bg-[#f26422] text-white"
                      : active
                        ? "border-[#f26422] bg-white text-[#f26422]"
                        : "border-[#E2E8F0] bg-white text-slate-400"
                  }`}
                >
                  {done ? "✓" : number}
                </div>
                <div
                  className={`mt-2 text-sm ${
                    active || done
                      ? "font-semibold text-[#f26422]"
                      : "text-slate-400"
                  }`}
                >
                  {label}
                </div>
              </div>
              {number < labels.length && (
                <div
                  className={`mt-4 h-0.5 ${
                    step > number ? "bg-[#f26422]" : "bg-[#E2E8F0]"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WizardActions = ({
  step,
  isSaving,
  saveLabel,
  onBack,
  onCancel,
  onNext,
  onSave,
   disabled,
}: {
  step: number;
  isSaving: boolean;
  saveLabel: string;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
  onSave: () => void;
  disabled?: boolean 
}) => (
  <div className="-mx-8 -mb-7 mt-7 grid grid-cols-3 gap-3 border-t border-[#E8EDF5] px-8 py-5">
    {step > 1 ? (
      <button
        type="button"
        onClick={onBack}
        
        className="h-12 rounded-lg border border-[#E2E8F0] font-semibold text-slate-500"
      >
        ← Back
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      onClick={onCancel}
      className="h-12 rounded-lg border border-[#E2E8F0] font-semibold text-slate-500"
    >
      Cancel
    </button>
    <button
      type="button"
      onClick={step === 3 ? onSave : onNext}
       disabled={isSaving || (step === 3 && !!disabled)} 
      className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#f26422] font-semibold text-white hover:bg-[#d9571d] disabled:opacity-60"
    >
      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
      {step === 3 ? saveLabel : "Next →"}
    </button>
  </div>
);

interface ComplexModalProps {
  district: DistrictRow;
  complex?: CourtComplexRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export const orangeInputClassName =
  "h-[46px] w-full rounded-lg border border-[#DDE6F1] bg-white px-4 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#f26422] focus:ring-2 focus:ring-orange-100";

const orangeTextAreaClassName =
  "min-h-[74px] w-full resize-none rounded-lg border border-[#DDE6F1] bg-white px-4 py-3 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#f26422] focus:ring-2 focus:ring-orange-100";

const OrangeInput = ({
  label,
  required,
  value,
  onChange,
  placeholder,
  type = "text",
  inputClassName = "",
  min,
  max,
  maxLength = undefined,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  inputClassName?: string;
  min?: number;
  max?: number;
  maxLength?: number;
}) => (
  <FormField label={label} required={required}>
    <input
      type={type}
      value={value}
      onChange={(e) => {
        const val = e.target.value;

        if (maxLength && val.length > maxLength) return;

        const num = Number(val);

        if (max !== undefined && num > max) return;

        onChange(val);
      }}
      placeholder={placeholder}
      className={clsx(inputClassName, orangeInputClassName)}
      min={type === "number" ? (min ?? 1) : undefined}
      max={type === "number" ? max : undefined}
      maxLength={maxLength}
    />
  </FormField>
);

const OrangeTextArea = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <FormField label={label}>
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={orangeTextAreaClassName}
    />
  </FormField>
);

const OrangeSelect = ({
  label,
  required,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
}) => (
  <FormField label={label} required={required}>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={orangeInputClassName}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </FormField>
);

const criminalEstablishmentTypes = [
  "District & Sessions Court",
  "Additional District & Sessions Court",
  "Chief Judicial Magistrate (CJM) Court",
  "Additional Chief Judicial Magistrate (ACJM) Court",
  "Judicial Magistrate First Class (JMFC) Court",
  "Sub-Divisional Judicial Magistrate (SDJM) Court",
  "Executive Magistrate Court",
];

const civilEstablishmentTypes = [
  "Principal District Judge Court",
  "Additional District Judge Court",
  "Civil Judge (Senior Division) Court",
  "Civil Judge (Junior Division) Court",
  "Munsiff Court",
];

const tribunalEstablishmentTypes = [
  "Family Court",
  "Fast Track Court",
  "Motor Accident Claims Tribunal (MACT)",
  "Labour Court",
];

const allEstablishmentTypes = [
  ...criminalEstablishmentTypes,
  ...civilEstablishmentTypes,
  ...tribunalEstablishmentTypes,
];

const normalizeText = (value?: string) =>
  (value || "").trim().toLowerCase().replace(/\s+/g, " ");

const findKnownEstablishmentType = (value?: string) => {
  const normalized = normalizeText(value);

  if (!normalized) return "";
  return (
    allEstablishmentTypes.find((type) => normalizeText(type) === normalized) ||
    ""
  );
};

const mapLegacyType = (value?: string) => {
  switch (value) {
    case "DISTRICT_COURT":
      return "District & Sessions Court";
    case "SESSION_COURT":
      return "District & Sessions Court";
    case "TRIBUNAL":
      return "Family Court";
    case "HIGH_COURT":
      return "Principal District Judge Court";
    default:
      return "";
  }
};

const inferJurisdictionSide = (establishmentType?: string) => {
  if (criminalEstablishmentTypes.includes(establishmentType || "")) {
    return "Criminal Side";
  }

  if (civilEstablishmentTypes.includes(establishmentType || "")) {
    return "Civil Side";
  }

  if (tribunalEstablishmentTypes.includes(establishmentType || "")) {
    return "Special / Tribunals";
  }

  return "";
};

const statusToFormValue = (status?: string) =>
  normalizeText(status) === "inactive" ? "Inactive" : "Active";

const statusToApiValue = (status?: string) =>
  normalizeText(status) === "inactive" ? "INACTIVE" : "ACTIVE";

const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const OrangeEstablishmentTypeSelect = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <FormField label="Establishment Type" required>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={orangeInputClassName}
    >
      <option value="">Select type...</option>
      <optgroup label="Criminal Side">
        {criminalEstablishmentTypes.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </optgroup>
      <optgroup label="Civil Side">
        {civilEstablishmentTypes.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </optgroup>
      <optgroup label="Special / Tribunals">
        {tribunalEstablishmentTypes.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </optgroup>
    </select>
  </FormField>
);

export const ComplexModal = ({
  district,
  complex,
  onClose,
  onSaved,
}: ComplexModalProps) => {
  const [form, setForm] = useState({
    complexName: complex?.complexName || "",
    locationArea: complex?.address || "",
    address: complex?.address || "",
    established: complex?.established || "",
    courtEstablishment: Boolean(complex?.courtEstablishment),
    status: complex?.status || "Active",
    description: complex?.description || "",
  });

  const isDirty = useFormDirty(
  {
    complexName:        complex?.complexName        || "",
    locationArea:       complex?.address            || "",
    address:            complex?.address            || "",
    established:        complex?.established        || "",
    courtEstablishment: String(Boolean(complex?.courtEstablishment)),
    status:             complex?.status             || "Active",
    description:        complex?.description        || "",
  },
  {
    ...form,
    courtEstablishment: String(form.courtEstablishment),  
  }
);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (complex && !isDirty) { toast.info("No changes detected"); return; }
    if (!form.complexName || !form.locationArea) {
      toast.error("Complex name and location are required");
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        complexName: form.complexName,
        established: form.established,
        address: form.address || form.locationArea,
        totalFloors: complex?.totalFloors || "",
        capacity: complex?.capacity || "",
        phone: complex?.phone || "",
        email: complex?.email || "",
        courtEstablishment: form.courtEstablishment,
        district: district._id,
        description: form.description,
        status: form.status,
      };
      const res = complex
        ? await updateComplex({ _id: complex._id, ...payload })
        : await createComplex(payload);

      if (res?.data?.status) {
        toast.success(res.data.message || "Court complex saved");
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save court complex");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save court complex");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
      <div className="flex max-h-[calc(100vh-48px)] w-full max-w-[675px] flex-col overflow-hidden rounded-[18px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#E8EDF5] px-[30px] py-6">
          <div>
            <h2 className="text-[22px] font-bold leading-7 text-slate-950">
              {complex ? "Edit Court Complex" : "Add Court Complex"}
            </h2>
            <p className="mt-1 text-base text-slate-400">
              {district.districtName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#DDE6F1] p-3 text-slate-500 transition hover:bg-slate-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-[30px] py-7">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <OrangeInput
              label="Complex Name"
              required
              value={form.complexName}
              onChange={(value) =>
                setForm({
                  ...form,
                  complexName: value.replace(/[^a-zA-Z\s]/g, ""),
                })
              }
              placeholder="e.g. Pune District Court Complex"
            />
            <OrangeInput
              label="Location / Area"
              required
              value={form.locationArea}
              onChange={(value) =>
                setForm({
                  ...form,
                  locationArea: value.replace(/[^a-zA-Z\s]/g, ""),
                })
              }
              placeholder="e.g. Shivajinagar, Pune"
            />
          </div>

          <div className="flex items-start gap-4 rounded-xl border border-[#ffb48e] bg-[#fff4ee] p-5">
            <input
              type="checkbox"
              checked={form.courtEstablishment}
              onChange={(event) =>
                setForm({ ...form, courtEstablishment: event.target.checked })
              }
              className="mt-1 h-5 w-5 rounded border-slate-300 accent-[#f26422]"
            />
            <span>
              <span className="block font-bold text-slate-900">
                This complex has Court Establishments
              </span>
              <span className="mt-1 block leading-6 text-slate-500">
                Check this if the complex contains separate judicial
                establishments (e.g. CJM Court, Civil Judge Court, JMFC).
                Courtrooms will be added inside each establishment rather than
                directly under the complex.
              </span>
            </span>
          </div>

          <OrangeTextArea
            label="Full Address"
            value={form.address}
            onChange={(value) =>
              setForm({
                ...form,
                address: value.replace(/[^a-zA-Z0-9\s,./()-]/g, ""),
              })
            }
            placeholder="e.g. Near Pune Railway Station, Shivajinagar, Pune 411005"
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label
                htmlFor="established"
                className="text-sm font-medium mb-2 block text-slate-700"
              >
                Established On
              </label>
              <DateInput
                value={form.established}
                onChange={(value) => setForm({ ...form, established: value })}
                max={getTodayDate()}
                placeholder="Select established date"
                theme={{
                  iconColor: "#f26422",
                  inputPadding: "12px",
                }}
              />
            </div>
            <OrangeSelect
              label="Status"
              required
              value={form.status}
              onChange={(value) => setForm({ ...form, status: value })}
              options={["Active", "Inactive"]}
            />
          </div>

          <OrangeTextArea
            label="Description"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
            placeholder="Brief details about this complex..."
          />
        </div>

        <div className="grid grid-cols-[1fr_1fr]  gap-3 border-t border-[#E8EDF5] px-7.5 py-5">
          <button
            type="button"
            onClick={onClose}
            className="h-12 rounded-lg border border-[#DDE6F1] font-semibold text-slate-500 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
              disabled={isSaving || (!!complex && !isDirty)}
            className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#f26422] font-semibold text-white transition hover:bg-[#d9571d] disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {complex ? "Update Court Complex" : "Save Court Complex"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface EstablishmentModalProps {
  district: DistrictRow;
  complex: CourtComplexRow;
  establishment?: EstablishmentRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export const EstablishmentModal = ({
  district,
  complex,
  establishment,
  onClose,
  onSaved,
}: EstablishmentModalProps) => {
  const inferredEstablishmentType =
    establishment?.establishmentType ||
    findKnownEstablishmentType(establishment?.code) ||
    mapLegacyType(establishment?.type);
  const inferredJurisdictionSide =
    establishment?.jurisdictionSide ||
    inferJurisdictionSide(inferredEstablishmentType);
  const [form, setForm] = useState({
    name: establishment?.name || "",
    establishmentType: inferredEstablishmentType,
    jurisdictionSide: inferredJurisdictionSide,
    courtIdentifier:
      establishment?.courtIdentifier ||
      (findKnownEstablishmentType(establishment?.code)
        ? ""
        : establishment?.code) ||
      "",
    presidingOfficerName:
      establishment?.presidingOfficerName ||
      establishment?.presidingOfficer ||
      "",
    officerDesignation: establishment?.officerDesignation || "",
    sanctionedCourtStrength: establishment?.sanctionedCourtStrength || "",
    establishedOn: toDateInputValue(establishment?.establishedOn),
    status: statusToFormValue(establishment?.status),
    description: establishment?.description || "",
  });

  const isDirty = useFormDirty(
  {
    name:                    establishment?.name                    || "",
    establishmentType:       inferredEstablishmentType              || "",
    jurisdictionSide:        inferredJurisdictionSide               || "",
    courtIdentifier:         establishment?.courtIdentifier         || "",
    presidingOfficerName:    establishment?.presidingOfficerName    || establishment?.presidingOfficer || "",
    officerDesignation:      establishment?.officerDesignation      || "",
    sanctionedCourtStrength: establishment?.sanctionedCourtStrength || "",
    establishedOn:           toDateInputValue(establishment?.establishedOn),
    status:                  statusToFormValue(establishment?.status),
    description:             establishment?.description             || "",
  },
  form
);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (establishment && !isDirty) { toast.info("No changes detected"); return; }
    if (!form.name || !form.establishmentType) {
      toast.error("Establishment name and type are required");
      return;
    }
    if (form?.sanctionedCourtStrength?.length > 3) {
      toast.error("Please enter a valid Sanctioned Court Strength.");
      return;
    }

    try {
      setIsSaving(true);
      const code = (form.courtIdentifier || establishment?.code || form.name)
        .trim()
        .toUpperCase();
      const payload = {
        name: form.name,
        code,
        establishmentType: form.establishmentType,
        jurisdictionSide:
          form.jurisdictionSide ||
          inferJurisdictionSide(form.establishmentType),
        courtIdentifier: form.courtIdentifier,
        presidingOfficerName: form.presidingOfficerName,
        officerDesignation: form.officerDesignation,
        sanctionedCourtStrength: form.sanctionedCourtStrength,
        establishedOn: form.establishedOn,
        description: form.description,
        status: statusToApiValue(form.status),
        district: district._id,
        complex: complex._id,
      };
      const res = establishment
        ? await updateEstablishment({ _id: establishment._id, ...payload })
        : await createEstablishment(payload);

      if (res?.data?.status) {
        toast.success(res.data.message || "Establishment saved");
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save establishment");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save establishment");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
      <div className="flex max-h-[calc(100vh-48px)] w-full max-w-[725px] flex-col overflow-hidden rounded-[18px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#E8EDF5] px-[30px] py-6">
          <div>
            <h2 className="text-[22px] font-bold leading-7 text-slate-950">
              {establishment ? "Edit Establishment" : "Add Establishment"}
            </h2>
            <p className="mt-1 text-base text-slate-400">
              {complex.complexName} · {district.districtName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#DDE6F1] p-3 text-slate-500 transition hover:bg-slate-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-[30px] py-7">
          <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2">
            <OrangeInput
              label="Establishment Name"
              required
              value={form.name}
              onChange={(value) => setForm({ ...form, name: value })}
              placeholder="e.g. CJM Court, Pune"
            />
            <OrangeEstablishmentTypeSelect
              value={form.establishmentType}
              onChange={(value) => {
                setForm({
                  ...form,
                  establishmentType: value,
                  jurisdictionSide:
                    form.jurisdictionSide || inferJurisdictionSide(value),
                });
              }}
            />
            <OrangeSelect
              label="Jurisdiction Side"
              value={form.jurisdictionSide}
              onChange={(value) =>
                setForm({ ...form, jurisdictionSide: value })
              }
              placeholder="Select..."
              options={["Criminal Side", "Civil Side", "Special / Tribunals"]}
            />
            <OrangeInput
              label="Court Number / Identifier"
              value={form.courtIdentifier}
              onChange={(value) => setForm({ ...form, courtIdentifier: value })}
              placeholder="e.g. 1st Court, Court No. 3"
            />
          </div>

          <div>
            <OrangeInput
              label="Presiding Officer Name"
              value={form.presidingOfficerName}
              onChange={(value) =>
                setForm({ ...form, presidingOfficerName: value })
              }
              placeholder="e.g. Sh. Anil Kumar Sharma"
            />
            <p className="mt-2 text-sm text-slate-400">
              The judge or magistrate currently heading this establishment
            </p>
          </div>

          <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2">
            <OrangeInput
              label="Officer Designation"
              value={form.officerDesignation}
              onChange={(value) =>
                setForm({ ...form, officerDesignation: value })
              }
              placeholder="e.g. Chief Judicial Magistrate"
            />
            <div>
              <OrangeInput
                label="Sanctioned Court Strength"
                value={form.sanctionedCourtStrength}
                onChange={(value) =>
                  setForm({ ...form, sanctionedCourtStrength: value })
                }
                placeholder="e.g. 4"
                min={0}
                max={100}
                maxLength={3}
              />
              <p className="mt-2 text-sm text-slate-400">
                Number of sanctioned judicial posts
              </p>
            </div>

            <div>
              <label
                htmlFor="established"
                className="text-sm font-medium mb-2 block text-slate-700"
              >
                Established Date
              </label>
              <DateInput
                value={form.establishedOn}
                onChange={(value) => setForm({ ...form, establishedOn: value })}
                max={getTodayDate()}
                placeholder="Select established date"
                theme={{
                  iconColor: "#f26422",
                  inputPadding: "12px",
                }}
              />
            </div>
            <OrangeSelect
              label="Status"
              required
              value={form.status}
              onChange={(value) => setForm({ ...form, status: value })}
              options={["Active", "Inactive"]}
            />
          </div>

          <OrangeTextArea
            label="Description / Notes"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
            placeholder="Any additional details about jurisdiction, special powers, or notes..."
          />
        </div>

        <div className="grid grid-cols-[1fr_1fr] gap-3 border-t border-[#E8EDF5] px-[30px] py-5">
          <button
            type="button"
            onClick={onClose}
            className="h-12 rounded-lg border border-[#DDE6F1] font-semibold text-slate-500 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
             disabled={isSaving || (!!establishment && !isDirty)}
            className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#f26422] font-semibold text-white transition hover:bg-[#d9571d] disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {establishment ? "Update Establishment" : "Save Establishment"}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CourtRoomModalProps {
  district: DistrictRow;
  complex: CourtComplexRow;
  establishment?: EstablishmentRow | null;
  room?: CourtRoomRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const facilityOptions = [
  "Wi-Fi",
  "Video Conferencing",
  "Digital Recording",
  "Wheelchair Access",
  "Air Conditioning",
  "Projector",
  "Interpreter Booth",
];

const JUDGE_PAGE_SIZE = 10;
const MIN_JUDGE_COUNT = 1;
const MAX_JUDGE_COUNT = 100;

const normalizeJudgeCountInput = (value: string) => {
  if (!value) return "";

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(MIN_JUDGE_COUNT);

  return String(
    Math.min(
      Math.max(Math.floor(numericValue), MIN_JUDGE_COUNT),
      MAX_JUDGE_COUNT,
    ),
  );
};

const clampJudgeCount = (value: string | number) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return MIN_JUDGE_COUNT;

  return Math.min(
    Math.max(Math.floor(numericValue), MIN_JUDGE_COUNT),
    MAX_JUDGE_COUNT,
  );
};

const mapJudgeOption = (judge: any): JudgeOption => ({
  value: String(judge.value || judge._id || ""),
  label:
    judge.label ||
    judge.fullName ||
    [judge.firstName, judge.lastName].filter(Boolean).join(" ") ||
    judge.name ||
    "Judge",
});

const mergeJudgeOptions = (
  currentOptions: JudgeOption[],
  nextOptions: JudgeOption[],
) => {
  const seen = new Set(currentOptions.map((option) => String(option.value)));
  const merged = [...currentOptions];

  nextOptions.forEach((option) => {
    const value = String(option.value);
    if (!value || seen.has(value)) return;

    seen.add(value);
    merged.push(option);
  });

  return merged;
};

export const CourtRoomModal = ({
  district,
  complex,
  establishment,
  room,
  onClose,
  onSaved,
}: CourtRoomModalProps) => {
  const [judgePage, setJudgePage] = useState(1);
  const [judgeTotalPages, setJudgeTotalPages] = useState(1);
  const [judgeKeyword, setJudgeKeyword] = useState("");
  const [debouncedJudgeKeyword, setDebouncedJudgeKeyword] = useState("");
  const [judges, setJudges] = useState<JudgeOption[]>([]);
  const [isLoadingJudges, setIsLoadingJudges] = useState(false);
  const [isLoadingMoreJudges, setIsLoadingMoreJudges] = useState(false);
  const initialJudgeIds = (room?.judges || [])
    .map((judge: any) => String(typeof judge === "string" ? judge : judge?._id))
    .filter(Boolean);

  const [form, setForm] = useState({
    courtRoomName: room?.courtRoomName || "",
    courtType: room?.courtType || "District Court",
    roomNumber: room?.roomNumber || "",
    floorNumber: room?.floorNumber || "",
    seatingCapacity: room?.seatingCapacity || "",
    judges: initialJudgeIds,
    facilities: room?.facilities || [],
    status: room?.status || "Active",
  });

  const isDirtyPrimitive = useFormDirty(
  {
    courtRoomName:   room?.courtRoomName   || "",
    courtType:       room?.courtType       || "District Court",
    roomNumber:      room?.roomNumber      || "",
    floorNumber:     room?.floorNumber     || "",
    seatingCapacity: room?.seatingCapacity || "",
    status:          room?.status          || "Active",
  },
  {
    courtRoomName:   form.courtRoomName,
    courtType:       form.courtType,
    roomNumber:      form.roomNumber,
    floorNumber:     form.floorNumber,
    seatingCapacity: form.seatingCapacity,
    status:          form.status,
  }
);

const isJudgesDirty = useMemo(() =>
  [...initialJudgeIds].sort().join(",") !== [...form.judges].sort().join(","),
  [initialJudgeIds, form.judges]
);

const isFacilitiesDirty = useMemo(() =>
  [...(room?.facilities || [])].sort().join(",") !== [...form.facilities].sort().join(","),
  [room?.facilities, form.facilities]
);

const isDirty = isDirtyPrimitive || isJudgesDirty || isFacilitiesDirty;
  const [selectedJudgeOptions, setSelectedJudgeOptions] = useState<
    SelectOption[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const judgeCapacity = form.seatingCapacity
    ? clampJudgeCount(form.seatingCapacity)
    : 0;
  const hasMoreJudges = judgePage < judgeTotalPages;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedJudgeKeyword(judgeKeyword.trim());
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [judgeKeyword]);

  const fetchJudges = useCallback(
    async (nextPage: number, append = false) => {
      append ? setIsLoadingMoreJudges(true) : setIsLoadingJudges(true);

      try {
        const response = await api.get(
          `/api/court/court-room-judges?limit=${JUDGE_PAGE_SIZE}&page=${nextPage}&keyword=${encodeURIComponent(
            debouncedJudgeKeyword,
          )}`,
        );
        const payload = response?.data;

        if (!payload?.status) {
          if (!append) setJudges([]);
          setJudgePage(1);
          setJudgeTotalPages(1);
          return;
        }

        const nextJudges = ((payload.data || []) as any[])
          .map(mapJudgeOption)
          .filter((judge) => judge.value);

        setJudges((currentJudges) =>
          append ? mergeJudgeOptions(currentJudges, nextJudges) : nextJudges,
        );
        setJudgePage(nextPage);
        setJudgeTotalPages(Number(payload.pagination?.totalPages) || 1);
      } catch {
        if (!append) setJudges([]);
        setJudgePage(1);
        setJudgeTotalPages(1);
      } finally {
        append ? setIsLoadingMoreJudges(false) : setIsLoadingJudges(false);
      }
    },
    [debouncedJudgeKeyword],
  );

  useEffect(() => {
    fetchJudges(1, false);
  }, [fetchJudges]);

  const loadMoreJudges = () => {
    if (!hasMoreJudges || isLoadingJudges || isLoadingMoreJudges) return;
    fetchJudges(judgePage + 1, true);
  };

  useEffect(() => {
    if (!form.judges.length || selectedJudgeOptions.length) return;

    const selectedOptions = form.judges.map((judgeId) => {
      const option = judges.find((judge) => String(judge.value) === judgeId);
      const roomJudge = (room?.judges || []).find((judge: any) => {
        const value = String(typeof judge === "string" ? judge : judge?._id);

        return value === judgeId;
      }) as any;

      return {
        value: judgeId,
        label:
          option?.label ||
          roomJudge?.label ||
          roomJudge?.fullName ||
          [roomJudge?.firstName, roomJudge?.lastName]
            .filter(Boolean)
            .join(" ") ||
          roomJudge?.name ||
          "Assigned Judge",
      };
    });

    setSelectedJudgeOptions(selectedOptions);
  }, [form.judges, judges, room?.judges, selectedJudgeOptions.length]);

  const handleJudgeSelectionChange = (value: any) => {
    const nextSelection = Array.isArray(value) ? value : [];

    if (judgeCapacity && nextSelection.length > judgeCapacity) {
      toast.error(
        `You can select only ${judgeCapacity} judge${judgeCapacity > 1 ? "s" : ""}`,
      );
      return;
    }

    setSelectedJudgeOptions(nextSelection);
    setForm((prev) => ({
      ...prev,
      judges: nextSelection.map((item) => String(item.value)),
    }));
  };

  const toggleFacility = (facility: string) => {
    setForm((prev) => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter((item) => item !== facility)
        : [...prev.facilities, facility],
    }));
  };

  const handleSave = async () => {
    if (room && !isDirty) { toast.info("No changes detected"); return; }
    if (
      !form.courtRoomName ||
      !form.roomNumber ||
      !form.floorNumber ||
      !form.seatingCapacity
    ) {
      toast.error(
        "Hall name, room number, floor, and judge count are required",
      );
      return;
    }
    if (judgeCapacity && form.judges.length > judgeCapacity) {
      toast.error(
        `You can select only ${judgeCapacity} judge${judgeCapacity > 1 ? "s" : ""}`,
      );
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        courtRoomName: form.courtRoomName,
        courtType: form.courtType,
        roomNumber: form.roomNumber,
        floorNumber: form.floorNumber,
        seatingCapacity: String(clampJudgeCount(form.seatingCapacity)),
        judges: form.judges,
        facilities: form.facilities,
        status: form.status,
        district: district._id,
        complex: complex._id,
        establishment: establishment?._id || "",
      };
      const res = room
        ? await updateCourtRoom({ _id: room._id, ...payload })
        : await createCourtRoom(payload);

      if (res?.data?.status) {
        toast.success(res.data.message || "Court room saved");
        (res.data.reassignedJudges || []).forEach((item: any) => {
          toast.warning(
            `${item.judgeName || "Judge"} removed from ${item.courtHallName || "previous courtroom"}`,
          );
        });
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save court room");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save court room");
    } finally {
      setIsSaving(false);
    }
  };

  const getFloorLabel = (count: number) => {
    if (count === 0) return "Ground Floor";

    const suffixes = ["th", "st", "nd", "rd"];
    const value = count % 100;

    return `${count}${
      suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]
    } Floor`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
      <div className="flex max-h-[calc(100vh-48px)] w-full max-w-[750px] flex-col overflow-hidden rounded-[18px] bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-[#E8EDF5] px-[30px] py-6">
          <div>
            <h2 className="text-[22px] font-bold leading-7 text-slate-950">
              {room ? "Edit Courtroom" : "Add Courtroom"}
            </h2>
            <p className="mt-1 text-base text-slate-400">
              {complex?.complexName} · {complex?.district?.districtName} ·
              {complex?.district?.stateName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#DDE6F1] p-3 text-slate-500 transition hover:bg-slate-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-[30px] py-7">
          <div className="grid grid-cols-1 gap-x-5 gap-y-6 md:grid-cols-2">
            <OrangeInput
              label="Hall Name"
              required
              value={form.courtRoomName}
              onChange={(value) => setForm({ ...form, courtRoomName: value })}
              placeholder="e.g. Courtroom 1 / Fast Track Court"
            />
            <OrangeInput
              label="Room Number"
              required
              value={form.roomNumber}
              onChange={(value) => setForm({ ...form, roomNumber: value })}
              placeholder="e.g. Room 204"
            />
            <FormField label="Floor" required>
              <select
                value={form.floorNumber}
                onChange={(event) =>
                  setForm({ ...form, floorNumber: event.target.value })
                }
                className={orangeInputClassName}
              >
                <option value="">Select floor</option>
                {[0, 1, 2, 3, 4, 5].map((count) => (
                  <option key={count} value={String(count)}>
                    {getFloorLabel(count)}
                  </option>
                ))}
              </select>
            </FormField>
            <OrangeInput
              label="Judge Count"
              required
              value={form.seatingCapacity}
              onChange={(value) => {
                const nextValue = normalizeJudgeCountInput(value);
                const nextCapacity = nextValue ? clampJudgeCount(nextValue) : 0;
                const nextSelectedJudges = nextCapacity
                  ? selectedJudgeOptions.slice(0, nextCapacity)
                  : selectedJudgeOptions;

                setSelectedJudgeOptions(nextSelectedJudges);
                setForm({
                  ...form,
                  seatingCapacity: nextValue,
                  judges: nextSelectedJudges.map((judge) =>
                    String(judge.value),
                  ),
                });
              }}
              placeholder="e.g. 1"
              type="number"
              inputClassName="show-spinner"
              min={MIN_JUDGE_COUNT}
              max={MAX_JUDGE_COUNT}
            />
          </div>

          <FormField label="Judges Assigned">
            <SearchableSelect
              options={judges}
              multiple
              value={selectedJudgeOptions}
              onChange={handleJudgeSelectionChange}
              onSearchChange={setJudgeKeyword}
              onLoadMore={loadMoreJudges}
              hasMore={hasMoreJudges}
              isLoadingMore={isLoadingMoreJudges}
              isLoadingOptions={isLoadingJudges}
              hideSelectedOptions
              placeholder="Search judge name..."
              searchPlaceholder="Search judge name..."
            />
            <p className="mt-2 text-xs text-slate-500">
              Selected {form.judges.length} / {judgeCapacity || 0} judges
            </p>
          </FormField>

          <FormField label="Facilities">
            <div className="flex flex-wrap gap-3">
              {facilityOptions.map((facility) => {
                const selected = form.facilities.includes(facility);

                return (
                  <button
                    type="button"
                    key={facility}
                    onClick={() => toggleFacility(facility)}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? "border-[#f26422] bg-[#fff4ee] text-[#c24e14]"
                        : "border-[#DDE6F1] bg-slate-50 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {facility}
                  </button>
                );
              })}
            </div>
          </FormField>

          <OrangeSelect
            label="Status"
            required
            value={form.status}
            onChange={(value) => setForm({ ...form, status: value })}
            options={["Active", "Inactive", "Under Maintenance"]}
          />
        </div>

        <div className="grid grid-cols-[1fr_1fr] gap-3 border-t border-[#E8EDF5] px-7.5 py-5">
          <button
            type="button"
            onClick={onClose}
            className="h-12 rounded-lg border border-[#DDE6F1] font-semibold text-slate-500 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
           disabled={isSaving || (!!room && !isDirty)} 
            className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#f26422] font-semibold text-white transition hover:bg-[#d9571d] disabled:opacity-60"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {room ? "Update Courtroom" : "Save Courtroom"}
          </button>
        </div>
      </div>
    </div>
  );
};
